import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * CallRating — the caller's own 1–5 rating of a call, asked as the last
 * question before the goodbye (ADR-200).
 *
 * NOT THE EVALUATOR'S SCORE, and the whole reason this renders at all.
 * `CallEvaluationSection` deliberately withholds its 1–5 category scores from
 * everyone but a system admin, because those are the AI's grade on its own work
 * and a customer-facing report card on it invites an argument the numbers cannot
 * settle (ADR-154). This number was given by the person who was on the call, so
 * that argument does not apply and it is shown to the practice.
 *
 * NULL RENDERS NOTHING — never "Not rated", never a zero, never an empty row of
 * stars. Following `CallEvaluationSection`'s rule: calm by default, every
 * element present only when it has something to say. Most calls have no rating
 * (a caller may decline the question or hang up before it), so a placeholder
 * would be the most common thing on the page and would read as a bad score
 * rather than an absent one.
 */

/**
 * Is there a rating here worth painting, and what does it say?
 *
 * Pure, and separate from the component, because the decision is the part with
 * a wrong answer — the rendering is a row of stars. DEFENSIVE AS WELL AS
 * CONDITIONAL: `GET /api/interactions/:id` returns the Drizzle row without a
 * `.parse()`, so this is the last place standing between an out-of-range value
 * and six stars or a negative row.
 */
export function callRatingDisplay(
  rating: number | null | undefined
): { rating: number; label: string } | null {
  if (typeof rating !== 'number' || !Number.isInteger(rating)) return null;
  if (rating < 1 || rating > 5) return null;
  return { rating, label: `${rating}/5` };
}

export function CallRating({
  rating,
  className,
}: {
  rating: number | null | undefined;
  className?: string;
}) {
  const display = callRatingDisplay(rating);
  if (!display) return null;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Caller rating
      </span>
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={cn(
              'h-3.5 w-3.5',
              n <= display.rating ? 'fill-primary text-primary' : 'text-muted-foreground/30'
            )}
          />
        ))}
      </span>
      {/* The number carries the meaning for a screen reader and for anyone who
          cannot tell a filled star from an empty one. */}
      <span className="text-sm text-muted-foreground">
        <span className="sr-only">Caller rated this call </span>
        {display.label}
      </span>
    </div>
  );
}
